import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab
} from '@mui/material';

const DAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const pad = (n) => String(n).padStart(2, '0');

// Expands a cron field such as "1-5,16-20", "7,9,11" or "*/2" into a sorted list of numbers
const expandField = (field, min, max) => {
  if (field === '*') return Array.from({ length: max - min + 1 }, (_, i) => min + i);

  const values = new Set();
  field.split(',').forEach((part) => {
    const [range, stepText] = part.split('/');
    const step = stepText ? Number(stepText) : 1;
    let [from, to] = range === '*' ? [min, max] : range.split('-').map(Number);
    if (to === undefined) to = stepText ? max : from;
    for (let v = from; v <= to; v += step) values.add(v);
  });
  return [...values].sort((a, b) => a - b);
};

// Shows a run of consecutive or evenly spaced numbers as a range, e.g. "1–5 & 16–20"
const formatRanges = (numbers) => {
  const groups = [];
  numbers.forEach((n) => {
    const last = groups[groups.length - 1];
    if (last && n === last[last.length - 1] + 1) last.push(n);
    else groups.push([n]);
  });
  return groups
    .map((g) => (g.length > 1 ? `${g[0]}–${g[g.length - 1]}` : `${g[0]}`))
    .join(' & ');
};

const formatTimes = (hours, minute) => {
  const mm = pad(minute);
  if (hours.length === 1) return `${pad(hours[0])}:${mm}`;

  const steps = hours.slice(1).map((h, i) => h - hours[i]);
  const evenlySpaced = steps.every((s) => s === steps[0]);
  const first = `${pad(hours[0])}:${mm}`;
  const last = `${pad(hours[hours.length - 1])}:${mm}`;

  if (evenlySpaced && steps[0] === 1 && hours.length > 2) return `stündlich ${first}–${last}`;
  if (evenlySpaced && hours.length > 2) return `alle ${steps[0]} Std., ${first}–${last}`;

  const all = hours.map((h) => `${pad(h)}:${mm}`);
  return `${all.slice(0, -1).join(', ')} & ${all[all.length - 1]}`;
};

const formatWeekdays = (dows) => {
  const normalized = [...new Set(dows.map((d) => d % 7))].sort((a, b) => a - b);
  if (normalized.length > 2 && normalized[normalized.length - 1] - normalized[0] === normalized.length - 1) {
    return `${DAYS[normalized[0]]}–${DAYS[normalized[normalized.length - 1]]}`;
  }
  return normalized.map((d) => DAYS[d]).join(', ');
};

// Turns a cron expression into a short German description. Falls back to the raw expression.
const describeCron = (cron) => {
  try {
    const [minuteField, hourField, domField, , dowField] = cron.trim().split(/\s+/);
    const minutes = expandField(minuteField, 0, 59);
    const hours = expandField(hourField, 0, 23);
    if (minutes.length !== 1) return cron;

    const time = formatTimes(hours, minutes[0]);

    if (dowField !== '*' && domField === '*') {
      return `${formatWeekdays(expandField(dowField, 0, 7))} · ${time}`;
    }
    if (dowField === '*' && domField === '*') return `Täglich · ${time}`;
    if (dowField === '*') {
      const days = expandField(domField, 1, 31);
      if (days.length === 1) return `${days[0]}. jedes Monats · ${time}`;
      return `Tage ${formatRanges(days)} · ${time}`;
    }
    return cron;
  } catch {
    return cron;
  }
};

// Returns the next time a cron expression fires, shown in German time (Europe/Berlin)
const findNextRun = (cron) => {
  try {
    const [minuteField, hourField, domField, monthField, dowField] = cron.trim().split(/\s+/);
    const minutes = expandField(minuteField, 0, 59);
    const hours = expandField(hourField, 0, 23);
    const doms = expandField(domField, 1, 31);
    const months = expandField(monthField, 1, 12);
    const dows = expandField(dowField, 0, 7).map((d) => d % 7);
    const domRestricted = domField !== '*';
    const dowRestricted = dowField !== '*';

    // Standard cron: when both day fields are restricted, either one may match
    const dayMatches = (date) => {
      const domOk = doms.includes(date.getUTCDate());
      const dowOk = dows.includes(date.getUTCDay());
      if (domRestricted && dowRestricted) return domOk || dowOk;
      if (domRestricted) return domOk;
      if (dowRestricted) return dowOk;
      return true;
    };

    const now = new Date();
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

    for (let offset = 0; offset < 800; offset++) {
      const day = new Date(today + offset * 86400000);
      if (!months.includes(day.getUTCMonth() + 1) || !dayMatches(day)) continue;

      for (const h of hours) {
        for (const m of minutes) {
          const candidate = new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), h, m));
          if (candidate > now) return candidate;
        }
      }
    }
    return null;
  } catch {
    return null;
  }
};

// Shows the earliest next run across all cron expressions of one workflow
const formatNextRun = (crons) => {
  const next = crons
    .map(findNextRun)
    .filter(Boolean)
    .sort((a, b) => a - b)[0];
  if (!next) return '–';
  return next.toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }) + ' Uhr';
};

// Combines several cron expressions into one description, e.g. "Tage 1–5 · 07:00, 09:00, 11:00"
const describeCrons = (crons) => {
  const parts = crons.map((cron) => describeCron(cron).split(' · '));
  const sameDay = parts.every((p) => p.length === 2 && p[0] === parts[0][0]);
  if (crons.length > 1 && sameDay) {
    return `${parts[0][0]} · ${parts.map((p) => p[1]).join(', ')}`;
  }
  return crons.map(describeCron).join(' | ');
};

const CronsPage = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCrons = async () => {
      try {
        const [circleResponse, githubResponse] = await Promise.all([
          axios.get('/api/crons'),
          axios.get('/api/crons/github')
        ]);
        const githubProject = {
          name: 'GitHub Actions',
          slug: 'github',
          schedules: githubResponse.data.workflows.reduce((rows, w) => {
            // One row per workflow file, with all of its cron entries together
            const existing = rows.find((r) => r.name === w.name && r.file === w.file);
            if (existing) {
              existing.crons.push(w.cron);
            } else {
              rows.push({
                name: w.name,
                file: w.file,
                crons: [w.cron],
                parameter: null,
                workflowEnabled: w.active,
                triggerDisabled: false,
                source: 'github'
              });
            }
            return rows;
          }, [])
        };
        const list = [...(circleResponse.data.projects || []), githubProject];
        setProjects(list);
        setSelectedProject((current) => current || list[0]?.name || null);
        setFetchedAt(circleResponse.data.fetchedAt);
      } catch (err) {
        setError(err.response?.data?.message || 'Crons konnten nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };
    loadCrons();
  }, []);

  const activeProject = projects.find((p) => p.name === selectedProject);
  const schedules = activeProject?.schedules || [];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Crons
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Geplante Läufe in CircleCI und GitHub Actions. Alle Zeiten sind UTC.
        {fetchedAt && ` Stand: ${new Date(fetchedAt).toLocaleString('de-DE')}`}
      </Typography>

      {loading && <CircularProgress size={24} />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <>
          <Tabs
            value={selectedProject || false}
            onChange={(_, value) => setSelectedProject(value)}
            sx={{ mb: 2 }}
          >
            {projects.map((project) => (
              <Tab
                key={project.slug}
                value={project.name}
                label={`${project.name} (${project.schedules.length})`}
              />
            ))}
          </Tabs>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Workflow</TableCell>
                  <TableCell>Laufzeit</TableCell>
                  <TableCell>Nächster Lauf</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((job) => (
                  <TableRow key={`${job.name}-${job.file || job.cron}`}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{job.name}</Typography>
                      {job.parameter && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          Parameter: <code>{job.parameter}</code>
                        </Typography>
                      )}
                      {job.file && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          Datei: <code>{job.file}</code>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{describeCrons(job.crons || [job.cron])}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatNextRun(job.crons || [job.cron])}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={job.workflowEnabled && !job.triggerDisabled ? 'Aktiv' : 'Aus'}
                        color={job.workflowEnabled && !job.triggerDisabled ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              "Aus" = läuft nicht: deaktiviert in GitHub bzw. Parameter false in CircleCI.
            </Typography>
          </Box>
        </>
      )}
    </Container>
  );
};

export default CronsPage;
